import { useEffect } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Maximize2 } from "lucide-react";

import { useBroadcastBlsChannel } from "../../application/useBroadcastBlsChannel";
import { useBlsHaptics } from "../../application/useBlsHaptics";
import PatientBlsView from "../components/PatientBlsView";

/**
 * The patient's screen of a standalone board, in a second window.
 *
 * Shows nothing but the stimulus and the way to stop it. Before a set starts
 * it says so, and offers full screen — the browser only grants that to a
 * press, so it cannot be done for the patient from the other window.
 */
export default function EmdrPatientScreenPage() {
    const { t } = useTranslation();
    const { sessionId = "" } = useParams<{ sessionId: string }>();
    const channel = useBroadcastBlsChannel(sessionId, "patient");
    const haptics = useBlsHaptics({ state: channel.state, cyclesNow: channel.cyclesNow });
    const { reportControllers } = channel;

    useEffect(() => {
        reportControllers(haptics.controllers);
    }, [haptics.controllers, reportControllers]);

    return (
        <div translate="no" className="room-surface flex w-full flex-col bg-room-base text-room-ink" data-testid="emdr-patient-screen">
            {channel.state ? (
                <div className="min-h-0 flex-1">
                    <PatientBlsView channel={channel} haptics={haptics} />
                </div>
            ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
                    <p role="status" className="text-sm text-room-ink-muted">
                        {t("emdr.board.waiting")}
                    </p>
                    <button
                        type="button"
                        onClick={() => void document.documentElement.requestFullscreen?.().catch(() => undefined)}
                        className="room-control"
                    >
                        <Maximize2 size={16} aria-hidden="true" />
                        <span>{t("emdr.board.fullscreen")}</span>
                    </button>
                </div>
            )}
        </div>
    );
}
